const S3_MEDIA_BASE = "https://vedic-meet-bucket.s3.ap-south-1.amazonaws.com/";

export const BANK_PAN_REQUEST_TYPES = [
  "BANK",
  "BANK_EDIT",
  "PAN",
  "PANCARD",
  "PAN_EDIT",
];

export function parseRequestJson(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function normalizeRequestType(value) {
  const type = String(value || "").trim().toUpperCase();
  if (type === "PAN") return "PANCARD";
  return type;
}

export function isBankRequestType(type) {
  const normalized = normalizeRequestType(type);
  return normalized === "BANK" || normalized === "BANK_EDIT";
}

export function isPanRequestType(type) {
  const normalized = normalizeRequestType(type);
  return normalized === "PANCARD" || normalized === "PAN_EDIT";
}

export function isEditAccessRequest(type) {
  const normalized = normalizeRequestType(type);
  return normalized === "BANK_EDIT" || normalized === "PAN_EDIT";
}

export function isBankPanRequestType(type) {
  return BANK_PAN_REQUEST_TYPES.includes(String(type || "").trim().toUpperCase());
}

export function hasCurrentData(currentData) {
  const data = parseRequestJson(currentData);
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  const ignored = new Set([
    "requestType",
    "reason",
    "consultantId",
    "documentType",
    "_id",
    "__v",
    "createdAt",
    "updatedAt",
  ]);
  return Object.keys(data).some((key) => {
    if (ignored.has(key)) return false;
    const value = data[key];
    if (value == null || value === "") return false;
    if (typeof value === "object") return false;
    return true;
  });
}

export function getRequestTypeLabel(type) {
  switch (normalizeRequestType(type)) {
    case "BANK":
      return "Bank details";
    case "BANK_EDIT":
      return "Bank edit access";
    case "PANCARD":
      return "PAN card";
    case "PAN_EDIT":
      return "PAN edit access";
    case "PHONE":
      return "Phone details";
    case "PRICE":
      return "Price details";
    case "FLAG":
      return "Flag";
    default:
      return type || "Request";
  }
}

export function getRequestTypeBadgeColor(type) {
  switch (normalizeRequestType(type)) {
    case "BANK":
      return "blue";
    case "BANK_EDIT":
      return "gold";
    case "PANCARD":
      return "purple";
    case "PAN_EDIT":
      return "magenta";
    default:
      return "default";
  }
}

export function isAdminOriginatedRequest(request) {
  const source = String(request?.source || "").toLowerCase();
  const review = String(request?.review || "").toLowerCase();
  return source === "admin" || review === "updated by admin";
}

export function getRequestKind(request) {
  const type = normalizeRequestType(
    request?.requestType || request?.newData?.requestType
  );
  if (type === "BANK_EDIT" || type === "PAN_EDIT") {
    return {
      type,
      kind: "access",
      kindLabel: "Access request",
      typeLabel: getRequestTypeLabel(type),
    };
  }
  if (type === "BANK" || type === "PANCARD") {
    if (isAdminOriginatedRequest(request)) {
      return {
        type,
        kind: "admin",
        kindLabel: "Admin update",
        typeLabel: getRequestTypeLabel(type),
      };
    }
    const isFirst = !hasCurrentData(request?.currentData);
    return {
      type,
      kind: isFirst ? "first" : "update",
      kindLabel: isFirst ? "First submission" : "Update",
      typeLabel: getRequestTypeLabel(type),
    };
  }
  return {
    type,
    kind: "other",
    kindLabel: "",
    typeLabel: getRequestTypeLabel(type),
  };
}

export function getApproveStatusLabel(status) {
  if (Number(status) === 1) return "Pending";
  if (Number(status) === 2) return "Approved";
  if (Number(status) === 3) return "Rejected";
  return "";
}

function unwrapMediaUrl(value) {
  if (!value || typeof value !== "string") return "";
  let url = value.trim();
  if (!url) return "";

  const nested = url.match(/^https?:\/\/[^/]+\/+(https?:\/\/.+)$/i);
  if (nested) url = nested[1];

  if (/^https?:\/\//i.test(url)) return url;
  return `${S3_MEDIA_BASE}${url.replace(/^\//, "")}`;
}

function pickImageCandidate(value) {
  if (!value || typeof value !== "string") return "";
  return unwrapMediaUrl(value);
}

export function resolveBankImageUrl(data) {
  const payload = parseRequestJson(data);
  if (!payload || typeof payload !== "object") return "";
  const fromAttachment = pickImageCandidate(payload.attachment);
  if (fromAttachment) return fromAttachment;
  if (
    typeof payload.attachmentUrl === "string" &&
    /^https?:\/\//i.test(payload.attachmentUrl.trim())
  ) {
    return unwrapMediaUrl(payload.attachmentUrl);
  }
  return "";
}

export function resolvePanImageUrl(data) {
  const payload = parseRequestJson(data);
  if (!payload || typeof payload !== "object") return "";
  return (
    pickImageCandidate(payload.panImage) ||
    pickImageCandidate(payload.pancardImage) ||
    ""
  );
}

export function fieldsDiffer(left, right) {
  return String(left || "").trim() !== String(right || "").trim();
}
